import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, UserPlus, Mail, Search, Check, Trash2 } from 'lucide-react';
import { createGroup, addMemberToGroup, inviteToGroup, getGroupName } from '../../services/groupsService';
import { getGroupNameSuggestions, createGroupNameWithTranslations } from '../../services/groupnamesService';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Modal for creating a new group
 */
const CreateGroupModal = ({ isOpen, onClose, contacts, userEmail, userId, onGroupCreated }) => {
    const { t, language } = useTranslation();
    const [step, setStep] = useState(1); // 1: name, 2: add members
    const [groupName, setGroupName] = useState('');
    const [createdGroup, setCreatedGroup] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [invitedEmails, setInvitedEmails] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Autosuggest states
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestion, setSelectedSuggestion] = useState(null);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    // Translation API fallback - if true, only allow selecting from suggestions
    const [translationDisabled, setTranslationDisabled] = useState(false);

    // Debounced search for suggestions
    useEffect(() => {
        // Don't search if a suggestion was just selected
        if (selectedSuggestion) {
            return;
        }

        if (!groupName.trim() || groupName.length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const timer = setTimeout(async () => {
            setLoadingSuggestions(true);
            try {
                const results = await getGroupNameSuggestions(groupName, language, 5);
                setSuggestions(results);
                setShowSuggestions(results.length > 0);
            } catch (err) {
                console.error('Error fetching suggestions:', err);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [groupName, language, selectedSuggestion]);

    // Handle suggestion selection
    const handleSelectSuggestion = (suggestion) => {
        setGroupName(suggestion.name);
        setSelectedSuggestion(suggestion);
        setShowSuggestions(false);
    };

    if (!isOpen) return null;

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            setError(t.groupModal?.groupNameRequired || 'Group name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let groupNameId;

            // If a suggestion was selected, use its id
            if (selectedSuggestion) {
                groupNameId = selectedSuggestion.id;
                console.log('Using existing groupNameId:', groupNameId);
            } else {
                // If translation is disabled, force user to select from suggestions
                if (translationDisabled) {
                    setError(t.groupModal?.selectFromSuggestions || 'Please select a group name from the suggestions');
                    setLoading(false);
                    return;
                }

                // Create new group name with translations in groupnames table
                console.log('Creating group name with translations for:', groupName.trim(), language, userId);
                const translateResult = await createGroupNameWithTranslations(groupName.trim(), language, userId);
                console.log('Translation result:', translateResult);

                if (translateResult.success && translateResult.groupNameId) {
                    groupNameId = translateResult.groupNameId;
                } else {
                    console.error('Failed to create group name:', translateResult.message);

                    // Check if it's a translation API error - enable fallback mode
                    const errorMsg = translateResult.message || '';
                    if (errorMsg.includes('Google Translate') || errorMsg.includes('Translation') || errorMsg.includes('API')) {
                        setTranslationDisabled(true);
                        setError(t.groupModal?.translationUnavailable || 'Translation service unavailable. Please select an existing group name from the suggestions.');
                    } else {
                        setError(translateResult.message || t.groupModal?.errorCreatingGroup || 'Error creating group name');
                    }
                    setLoading(false);
                    return;
                }
            }

            // Create group with only groupNameId (translations are in groupnames table)
            const groupData = {
                groupNameId: groupNameId,
                createdById: userId
            };

            console.log('Final groupData being sent:', groupData);

            const group = await createGroup(groupData);
            setCreatedGroup(group);
            setStep(2);
        } catch (err) {
            console.error('Error creating group:', err);
            setError(t.groupModal?.errorCreatingGroup || 'Error creating group');
        } finally {
            setLoading(false);
        }
    };

    const handleAddMember = async (contact) => {
        if (selectedMembers.some(m => m.id === contact.id)) return;

        setSelectedMembers(prev => [...prev, contact]);

        if (createdGroup) {
            try {
                await addMemberToGroup(createdGroup.groupUuid, contact);
            } catch (err) {
                console.error('Error adding member:', err);
                setError(t.groupModal?.errorAddingMember || 'Error adding member');
            }
        }
    };

    const handleRemoveMember = (memberId) => {
        setSelectedMembers(prev => prev.filter(m => m.id !== memberId));
    };

    const handleInviteByEmail = async () => {
        const email = inviteEmail.trim().toLowerCase();

        if (!email) return;

        // Basic email validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError(t.groupModal?.invalidEmail || 'Invalid email address');
            return;
        }

        // Check if already invited
        if (invitedEmails.includes(email)) {
            setError(t.groupModal?.alreadyInvited || 'This address is already invited');
            return;
        }

        // Check if already a member (from Linkoo contacts)
        if (selectedMembers.some(m => m.email?.toLowerCase() === email)) {
            setError(t.groupModal?.alreadyMember || 'This person is already a member');
            return;
        }

        setInvitedEmails(prev => [...prev, email]);

        if (createdGroup) {
            try {
                await inviteToGroup(createdGroup.groupUuid, email);
            } catch (err) {
                console.error('Error inviting:', err);
                setError(t.groupModal?.errorInviting || 'Error inviting');
            }
        }

        setInviteEmail('');
        setError('');
    };

    const handleRemoveInvitation = (email) => {
        setInvitedEmails(prev => prev.filter(e => e !== email));
    };

    const handleFinish = () => {
        onGroupCreated?.(createdGroup);
        handleClose();
    };

    const handleClose = () => {
        setStep(1);
        setGroupName('');
        setCreatedGroup(null);
        setSearchTerm('');
        setInviteEmail('');
        setSelectedMembers([]);
        setInvitedEmails([]);
        setError('');
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedSuggestion(null);
        setTranslationDisabled(false);
        onClose();
    };

    // Filter contacts based on search
    const filteredContacts = contacts.filter(c => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            `${c.firstName} ${c.lastName}`.toLowerCase().includes(search) ||
            c.email?.toLowerCase().includes(search) ||
            c.company?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-linkoo-purple to-linkoo-pink p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Users className="w-6 h-6" />
                            <h2 className="text-xl font-bold">
                                {step === 1 ? (t.groupModal?.createGroup || 'Create group') : (t.groupModal?.addMembers || 'Add members')}
                            </h2>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    {step === 2 && createdGroup && (
                        <p className="mt-2 text-white/80">{t.groupModal?.group || 'Group'}: {getGroupName(createdGroup, language)}</p>
                    )}
                </div>

                {/* Content */}
                <div className={`p-6 max-h-[60vh] ${showSuggestions ? 'overflow-visible' : 'overflow-y-auto'}`}>
                    {error && !translationDisabled && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Step 1: Group Name */}
                    {step === 1 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t.groupModal?.groupName || 'Group name'}
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={groupName}
                                    onChange={(e) => {
                                        setGroupName(e.target.value);
                                        setSelectedSuggestion(null);
                                    }}
                                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                    placeholder={t.groupModal?.groupNamePlaceholder || 'Ex: Family, Colleagues, Friends...'}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkoo-purple/20 focus:border-linkoo-purple"
                                    autoFocus
                                />
                                {loadingSuggestions && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="w-4 h-4 border-2 border-linkoo-purple border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                )}

                                {/* Suggestions dropdown */}
                                {showSuggestions && suggestions.length > 0 && (
                                    <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                        {suggestions.map((suggestion, index) => (
                                            <button
                                                key={suggestion.id || index}
                                                type="button"
                                                onClick={() => handleSelectSuggestion(suggestion)}
                                                className="w-full px-4 py-2 text-left hover:bg-linkoo-purple/10 transition-colors flex items-center justify-between"
                                            >
                                                <span className="text-gray-900">{suggestion.name}</span>
                                                <Check className="w-4 h-4 text-linkoo-purple opacity-0 hover:opacity-100" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Show selected suggestion info */}
                            {selectedSuggestion && (
                                <p className="mt-2 text-xs text-green-600">
                                    {t.groupModal?.existingSuggestion || 'Existing group name selected - translations available'}
                                </p>
                            )}

                            {/* Warning when translation is disabled */}
                            {translationDisabled && !selectedSuggestion && (
                                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                    <p className="text-xs text-amber-700">
                                        {t.groupModal?.translationDisabledInfo || 'The translation service is temporarily unavailable. Please select an existing group name from the suggestions above.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Add Members */}
                    {step === 2 && (
                        <div className="space-y-6">
                            {/* Selected members */}
                            {(selectedMembers.length > 0 || invitedEmails.length > 0) && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                                        {t.groupModal?.members || 'Members'} ({selectedMembers.length + invitedEmails.length})
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedMembers.map(member => (
                                            <span
                                                key={member.id}
                                                className="inline-flex items-center gap-1 bg-linkoo-purple/10 text-linkoo-purple px-3 py-1 rounded-full text-sm"
                                            >
                                                {member.firstName} {member.lastName}
                                                <button
                                                    onClick={() => handleRemoveMember(member.id)}
                                                    className="ml-1 hover:bg-linkoo-purple/20 rounded-full p-0.5"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                        {invitedEmails.map(email => (
                                            <span
                                                key={email}
                                                className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm"
                                            >
                                                <Mail className="w-3 h-3" />
                                                {email}
                                                <button
                                                    onClick={() => handleRemoveInvitation(email)}
                                                    className="ml-1 hover:bg-orange-200 rounded-full p-0.5"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Add from Linkoo contacts */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">
                                    {t.groupModal?.addFromContacts || 'Add from my contacts'}
                                </h3>
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder={t.groupModal?.searchContacts || 'Search...'}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkoo-purple/20 focus:border-linkoo-purple text-sm"
                                    />
                                </div>
                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                                    {filteredContacts.length === 0 ? (
                                        <p className="p-3 text-sm text-gray-500 text-center">
                                            {t.groupModal?.noContactsFound || 'No contacts found'}
                                        </p>
                                    ) : (
                                        filteredContacts.map(contact => {
                                            const isSelected = selectedMembers.some(m => m.id === contact.id);
                                            return (
                                                <button
                                                    key={contact.id}
                                                    onClick={() => handleAddMember(contact)}
                                                    disabled={isSelected}
                                                    className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                                                        isSelected ? 'bg-green-50' : ''
                                                    }`}
                                                >
                                                    <div>
                                                        <p className="font-medium text-gray-900 text-sm">
                                                            {contact.firstName} {contact.lastName}
                                                        </p>
                                                        {contact.email && (
                                                            <p className="text-xs text-gray-500">{contact.email}</p>
                                                        )}
                                                    </div>
                                                    {isSelected ? (
                                                        <Check className="w-5 h-5 text-green-500" />
                                                    ) : (
                                                        <UserPlus className="w-5 h-5 text-gray-400" />
                                                    )}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* Invite by email */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">
                                    {t.groupModal?.inviteByEmail || 'Invite by email'}
                                </h3>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="email"
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleInviteByEmail()}
                                            placeholder={t.groupModal?.emailPlaceholder || 'address@email.com'}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-linkoo-purple/20 focus:border-linkoo-purple text-sm"
                                        />
                                    </div>
                                    <button
                                        onClick={handleInviteByEmail}
                                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                                    >
                                        {t.groupModal?.invite || 'Invite'}
                                    </button>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">
                                    {t.groupModal?.inviteEmailSent || 'An invitation email will be sent to this address'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                        {t.common?.cancel || 'Cancel'}
                    </button>
                    {step === 1 ? (
                        <button
                            onClick={handleCreateGroup}
                            disabled={loading || !groupName.trim() || (translationDisabled && !selectedSuggestion)}
                            className="px-6 py-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {loading ? (t.groupModal?.creating || 'Creating...') : (t.groupModal?.continue || 'Continue')}
                        </button>
                    ) : (
                        <button
                            onClick={handleFinish}
                            className="px-6 py-2 bg-gradient-to-r from-linkoo-purple to-linkoo-pink text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                        >
                            {t.groupModal?.finish || 'Finish'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateGroupModal;
